import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HiOutlineCheckCircle, HiOutlineExclamation } from "react-icons/hi";

import {
  getLegalCertificateHandover,
  recordLegalCertificateHandover,
  type LegalCertificateHandoverPreview,
} from "../../api/legalCertificate";

export const LEGAL_CERTIFICATE_HANDOVER_FORM_ID =
  "legal-certificate-handover-form";

const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

const KIT_ITEMS: { key: string; label: string }[] = [
  { key: "coveringNote", label: "Covering Note" },
  { key: "originalLegalCertificate", label: "Original copy of Legal Certificate" },
  {
    key: "registrationCertificate",
    label: "Colour Printout of Registration Certificate",
  },
  { key: "framedCertificate", label: "Framed Certificate" },
];

function CheckIcon() {
  return (
    <span
      className="flex items-center justify-center w-5 h-5 rounded-md bg-[#22C55E] flex-shrink-0"
      aria-hidden
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.5 6.2 L4.8 8.5 L9.5 3.3"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </span>
  );
}

function ConfirmCheckbox({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <span
      className="flex items-center justify-center w-6 h-6 flex-shrink-0"
      aria-hidden
    >
      <span
        className={`flex items-center justify-center w-5 h-5 rounded-md border ${
          checked
            ? "bg-[#222222] border-[#222222]"
            : "bg-white border-[#D6D8DB]"
        } ${disabled ? "opacity-60" : ""}`}
      >
        {checked && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 6.2 L4.8 8.5 L9.5 3.3"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </span>
  );
}

function formatHandoverTimestamp(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} at ${hh}:${mi}`;
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function LegalCertificateHandOverKitToClientView({
  leadId,
  onSuccess,
}: Props) {
  const [preview, setPreview] = useState<LegalCertificateHandoverPreview | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLegalCertificateHandover(leadId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setConfirmed(data.isHandedOver);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load Hand Over Kit status"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const prerequisitesMet =
    !!preview?.isLegalCertificateUploaded && !!preview?.isCoveringNoteGenerated;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving || !preview) return;

    if (preview.isHandedOver) {
      onSuccess?.(leadId);
      return;
    }

    if (!preview.isLegalCertificateUploaded) {
      toast.error(
        "Upload the scanned Legal Certificate (Step 1) before handing over the kit."
      );
      return;
    }
    if (!preview.isCoveringNoteGenerated) {
      toast.error(
        "Generate the Covering Note PDF (Step 2) before handing over the kit."
      );
      return;
    }
    if (!confirmed) {
      toast.error("Please confirm that the kit has been handed over.");
      return;
    }

    setSaving(true);
    try {
      const data = await recordLegalCertificateHandover(leadId);
      setPreview(data);
      setConfirmed(true);
      toast.success("Kit handed over to client.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record handover"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading Hand Over Kit status...</p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Application details not found. Please complete the previous Legal
          Certificate steps first.
        </p>
      </div>
    );
  }

  const tmNo = preview.applicationNumber || "—";
  const mark = preview.mark;

  return (
    <form
      id={LEGAL_CERTIFICATE_HANDOVER_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Header / context */}
      <div className="flex flex-col gap-1.5">
        <p
          className="font-semibold text-[#101111]"
          style={{
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "0.01em",
          }}
        >
          Hand Over Kit to Client
        </p>
        <p
          className="font-normal text-[#7D7D7D]"
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            letterSpacing: "0.04em",
          }}
        >
          Confirm that the kit has been physically handed over to the client.
        </p>
      </div>

      {/* TM No. + Mark chip */}
      <div className="flex flex-col md:flex-row md:items-start w-full gap-4 md:gap-5">
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <label
            className="text-xs font-normal leading-5 text-[#6D6D6D]"
            style={LABEL_FEATURES}
          >
            TM Number
          </label>
          <div
            className="box-border flex flex-row items-center gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-[#EEEEEE] border border-[#E0E0E0] rounded-[6px] text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]"
            style={LABEL_FEATURES}
            title={mark ? `${tmNo} · ${mark}` : tmNo}
          >
            <span className="min-w-0 flex-1 truncate">{tmNo}</span>
          </div>
        </div>
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <label
            className="text-xs font-normal leading-5 text-[#6D6D6D]"
            style={LABEL_FEATURES}
          >
            Mark
          </label>
          <div
            className="box-border flex flex-row items-center gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-[#EEEEEE] border border-[#E0E0E0] rounded-[6px] text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]"
            style={LABEL_FEATURES}
          >
            <span className="min-w-0 flex-1 truncate">{mark || "—"}</span>
          </div>
        </div>
      </div>

      {/* Kit contents – read-only summary of what is being handed over */}
      <div className="flex flex-col gap-3 self-stretch">
        <span
          className="text-sm font-medium leading-5 text-[#222222]"
          style={LABEL_FEATURES}
        >
          The kit contains the following
        </span>

        <div className="flex flex-col gap-2 self-stretch">
          {KIT_ITEMS.map((item) => (
            <div
              key={item.key}
              className="flex flex-row items-center gap-2 self-stretch px-2.5 py-2.5 min-h-11 bg-white border border-[#EEEEEE] rounded-[6px]"
            >
              <CheckIcon />
              <span
                className="text-xs leading-5 text-[#222222]"
                style={LABEL_FEATURES}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Prerequisite warnings */}
      {!prerequisitesMet && !preview.isHandedOver && (
        <div className="flex flex-col gap-2">
          {!preview.isLegalCertificateUploaded && (
            <div
              className="flex items-start gap-2 px-3 py-2.5 rounded-[6px] border border-[#F1C40F]/50 bg-[#FFF8E1]"
              role="status"
            >
              <HiOutlineExclamation
                className="w-4 h-4 mt-0.5 text-[#B45309] flex-shrink-0"
                strokeWidth={2}
              />
              <p
                className="text-xs leading-5 text-[#7A4A00]"
                style={LABEL_FEATURES}
              >
                Scanned Legal Certificate has not been uploaded. Complete{" "}
                <span className="font-medium">
                  Status &rsaquo; Legal Certificate Received
                </span>{" "}
                before recording the handover.
              </p>
            </div>
          )}
          {!preview.isCoveringNoteGenerated && (
            <div
              className="flex items-start gap-2 px-3 py-2.5 rounded-[6px] border border-[#F1C40F]/50 bg-[#FFF8E1]"
              role="status"
            >
              <HiOutlineExclamation
                className="w-4 h-4 mt-0.5 text-[#B45309] flex-shrink-0"
                strokeWidth={2}
              />
              <p
                className="text-xs leading-5 text-[#7A4A00]"
                style={LABEL_FEATURES}
              >
                Covering Note PDF has not been generated. Complete{" "}
                <span className="font-medium">
                  Status &rsaquo; Prepare Kit of Legal Certificate
                </span>{" "}
                before recording the handover.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Already handed over – success card */}
      {preview.isHandedOver && (
        <div
          className="flex items-start gap-2 px-3 py-2.5 rounded-[6px] border border-[#22C55E]/30 bg-[#F0FDF4]"
          role="status"
        >
          <HiOutlineCheckCircle
            className="w-5 h-5 text-[#16A34A] flex-shrink-0"
            strokeWidth={2}
          />
          <div className="flex flex-col gap-0.5 min-w-0">
            <p
              className="text-xs font-medium leading-5 text-[#15803D]"
              style={LABEL_FEATURES}
            >
              Kit handed over to client
            </p>
            {preview.handedOverToClientAt && (
              <p
                className="text-[11px] leading-4 text-[#15803D]/80"
                style={LABEL_FEATURES}
              >
                Recorded on {formatHandoverTimestamp(preview.handedOverToClientAt)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Confirmation toggle */}
      {!preview.isHandedOver && (
        <label
          className={`flex flex-row items-start gap-2 self-stretch px-2.5 py-2.5 min-h-11 bg-white border rounded-[6px] ${
            prerequisitesMet
              ? "border-[#EEEEEE] cursor-pointer"
              : "border-[#EEEEEE] opacity-60 cursor-not-allowed"
          }`}
        >
          <ConfirmCheckbox
            checked={confirmed}
            onChange={setConfirmed}
            disabled={!prerequisitesMet || saving}
          />
          <span
            className="text-xs leading-5 text-[#222222] pt-0.5"
            style={LABEL_FEATURES}
          >
            I confirm that the above kit has been handed over to the client.
          </span>
        </label>
      )}

    </form>
  );
}
